/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { service } from '@ember/service';
import { attr, belongsTo, hasMany, type AsyncBelongsTo, type AsyncHasMany } from '@ember-data/model';
import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import type CompanyConfigurationModel from 'garaje/models/company-configuration';
import type EmployeesFilter from 'garaje/models/employees-filter';
import type GoogleAppModel from 'garaje/models/google-app';
import type IftttIntegration from 'garaje/models/ifttt-integration';
import type OktaIntegrationModel from 'garaje/models/okta-integration';
import type OneLoginModel from 'garaje/models/one-login';
import type SalesforceIntegration from 'garaje/models/salesforce-integration';
import type Saml from 'garaje/models/saml';
import type ScimIntegrationModel from 'garaje/models/scim-integration';
import type SlackIntegrationModel from 'garaje/models/slack-integration';
import type SubscriptionModel from 'garaje/models/subscription';
import UserDocumentLinkable from 'garaje/models/user-document-linkable';
import type VfdCompanyConfiguration from 'garaje/models/vfd-company-configuration';
import type ZapierIntegrationModel from 'garaje/models/zapier-integration';
import adapter from 'garaje/utils/decorators/adapter';
import moment from 'moment-timezone';

// As of Ember v3.28, polymorphic associations (like UserDocumentLinkable)
// can only be declared on native model classes through inheritance.
// https://guides.emberjs.com/release/models/relationships/#toc_polymorphism
// ("Classic" Ember Objects support multiple polymorphic associations through
// Ember Mixin.) Conversations on the Ember Data channel in Discord indicate
// hope for supporting polymorphic associations with "plain" relationship
// declarations (no inheritance or mixins) "soon." [revisit polymorphic]
// ~ Dana F. 2021.Dec.14

@adapter('v2')
class Company extends UserDocumentLinkable {
  @service declare store: Store;

  @hasMany('company-role', { async: true, inverse: 'company' })
  declare companyRoles: AsyncHasMany<Model>;

  @belongsTo('google-app', { async: true, inverse: 'company' })
  declare googleApp: AsyncBelongsTo<GoogleAppModel>;

  @belongsTo('ifttt-integration', { async: true, inverse: 'company' })
  declare iftttIntegration: AsyncBelongsTo<IftttIntegration>;

  @belongsTo('okta-integration', { async: true, inverse: 'company' })
  declare oktaIntegration: AsyncBelongsTo<OktaIntegrationModel>;

  @belongsTo('one-login', { async: true, inverse: 'company' })
  declare oneLogin: AsyncBelongsTo<OneLoginModel>;

  @hasMany('location', { async: true, inverse: 'company' })
  declare locations: AsyncHasMany<Model>;

  @belongsTo('salesforce-integration', { async: true, inverse: 'company' })
  declare salesforceIntegration: AsyncBelongsTo<SalesforceIntegration>;

  @belongsTo('saml', { async: true, inverse: 'company' })
  declare saml: AsyncBelongsTo<Saml>;

  @belongsTo('scim-integration', { async: true, inverse: 'company' })
  declare scimIntegration: AsyncBelongsTo<ScimIntegrationModel>;

  @belongsTo('slack-integration', { async: true, inverse: 'company' })
  declare slackIntegration: AsyncBelongsTo<SlackIntegrationModel>;

  @belongsTo('subscription', { async: true, inverse: null })
  declare deliveriesSubscription: AsyncBelongsTo<SubscriptionModel>;

  @belongsTo('zapier-integration', { async: true, inverse: 'company' })
  declare zapierIntegration: AsyncBelongsTo<ZapierIntegrationModel>;

  @belongsTo('company-configuration', { async: true, inverse: 'company' })
  declare companyConfiguration: AsyncBelongsTo<CompanyConfigurationModel>;

  @belongsTo('vfd-company-configuration', { async: true, inverse: 'company' })
  declare vfdCompanyConfiguration: AsyncBelongsTo<VfdCompanyConfiguration>;

  @attr()
  declare adhocPlugins: string[];

  @attr('boolean')
  declare active: boolean;

  @attr('date')
  declare createdAt: Date;

  @attr('string')
  declare employeesLastSyncAt: string;

  @attr('string')
  declare employeesSyncState: string;

  @attr('string')
  declare name: string;

  @attr('string')
  declare onboardingLocationsCount: string;

  @attr('string')
  declare onboardingStatus: string;

  @attr('string')
  declare planIntent: string;

  @attr('boolean')
  declare samlConnected: boolean;

  @attr('boolean', { defaultValue: false })
  declare gtalkEnabled: boolean;

  @attr('boolean', { defaultValue: false })
  declare callEnabled: boolean;

  @attr('boolean', { defaultValue: false })
  declare isBeta: boolean;

  @attr('boolean', { defaultValue: false })
  declare office365Enabled: boolean;

  @attr('boolean', { defaultValue: false })
  declare connectCustomer: boolean;

  @attr('number')
  declare visitorLogEntriesThisMonth: number | undefined;

  get synchronizing(): boolean {
    return ['scheduled', 'synchronizing'].indexOf(this.employeesSyncState) >= 0;
  }

  get syncedSince(): string {
    return moment(this.employeesLastSyncAt).fromNow();
  }

  @or('oneLogin.content', 'googleApp.content', 'oktaIntegration.content', 'scimIntegration.content')
  declare directorySyncProvider: boolean;

  @computed('googleApp.content', 'store')
  get employeesFilter(): EmployeesFilter | false {
    const integration = this.googleApp.content;
    if (!integration) {
      // preserving behavior from before
      return false;
    }

    const filter: GoogleAppModel['filters'] & { id?: string; locations?: Record<string, unknown>; query?: unknown } =
      integration.filters;

    filter.id = integration.id;

    switch (filter.type) {
      case 'locations':
        filter.locations = filter.data;
        break;
      case 'company':
        filter.query = filter.data?.['query'];
        break;
    }
    const employeeFilter = this.store.peekAll('employees-filter').slice()[0];

    if (employeeFilter) employeeFilter.rollbackAttributes();

    return <EmployeesFilter>this.store.push(this.store.normalize('employees-filter', filter));
  }
}

export default Company;

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    company: Company;
  }
}
